import "core-js/modules/es6.function.name";
import _classCallCheck from "/home/vsts/work/1/s/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/home/vsts/work/1/s/node_modules/@babel/runtime/helpers/esm/createClass";
// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT license.
import { connectivity, type } from "../common.speech/Exports";
import { AudioSourceErrorEvent, AudioSourceInitializingEvent, AudioSourceOffEvent, AudioSourceReadyEvent, AudioStreamNodeAttachedEvent, AudioStreamNodeAttachingEvent, AudioStreamNodeDetachedEvent, AudioStreamNodeErrorEvent, ChunkedArrayBufferStream, createNoDashGuid, Deferred, Events, EventSource, PromiseHelper } from "../common/Exports";
import { AudioStreamFormat } from "../sdk/Audio/AudioStreamFormat";
export var FileAudioSource = /*#__PURE__*/function () {
  function FileAudioSource(file, audioSourceId) {
    var _this = this;

    _classCallCheck(this, FileAudioSource);

    this.privStreams = {};

    this.turnOn = function () {
      if (typeof FileReader === "undefined") {
        var errorMsg = "Browser does not support FileReader.";

        _this.onEvent(new AudioSourceErrorEvent(errorMsg, "")); // initialization error - no streamid at this point


        return PromiseHelper.fromError(errorMsg);
      } else if (_this.privFile.name.lastIndexOf(".wav") !== _this.privFile.name.length - 4) {
        var _errorMsg = _this.privFile.name + " is not supported. Only WAVE files are allowed at the moment.";

        _this.onEvent(new AudioSourceErrorEvent(_errorMsg, ""));

        return PromiseHelper.fromError(_errorMsg);
      }

      _this.onEvent(new AudioSourceInitializingEvent(_this.privId)); // no stream id


      _this.onEvent(new AudioSourceReadyEvent(_this.privId));

      return PromiseHelper.fromResult(true);
    };

    this.id = function () {
      return _this.privId;
    };

    this.attach = function (audioNodeId) {
      _this.onEvent(new AudioStreamNodeAttachingEvent(_this.privId, audioNodeId));

      return _this.upload(audioNodeId).onSuccessContinueWith(function (streamReader) {
        _this.onEvent(new AudioStreamNodeAttachedEvent(_this.privId, audioNodeId));

        return {
          detach: function detach() {
            streamReader.close();
            delete _this.privStreams[audioNodeId];

            _this.onEvent(new AudioStreamNodeDetachedEvent(_this.privId, audioNodeId));

            _this.turnOff();
          },
          id: function id() {
            return audioNodeId;
          },
          read: function read() {
            return streamReader.read();
          }
        };
      });
    };

    this.detach = function (audioNodeId) {
      if (audioNodeId && _this.privStreams[audioNodeId]) {
        _this.privStreams[audioNodeId].close();

        delete _this.privStreams[audioNodeId];

        _this.onEvent(new AudioStreamNodeDetachedEvent(_this.privId, audioNodeId));
      }
    };

    this.turnOff = function () {
      for (var streamId in _this.privStreams) {
        if (streamId) {
          var stream = _this.privStreams[streamId];

          if (stream && !stream.isClosed) {
            stream.close();
          }
        }
      }

      _this.onEvent(new AudioSourceOffEvent(_this.privId)); // no stream now


      return PromiseHelper.fromResult(true);
    };

    this.readHeader = function () {
      // Read the wave header.
      var header = _this.privFile.slice(0, 44);

      var headerReader = new FileReader();
      var headerResult = new Deferred();

      var processHeader = function processHeader(event) {
        var header = event.target.result;
        var view = new DataView(header); // RIFF 4 bytes.

        var riff = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));

        if ("RIFF" !== riff) {
          headerResult.reject("Invalid WAV header in file, RIFF was not found");
        } // length, 4 bytes
        // RIFF Type & fmt 8 bytes


        var type = String.fromCharCode(view.getUint8(8), view.getUint8(9), view.getUint8(10), view.getUint8(11), view.getUint8(12), view.getUint8(13), view.getUint8(14));

        if ("WAVEfmt" !== type) {
          headerResult.reject("Invalid WAV header in file, WAVEfmt was not found");
        }

        var channelCount = view.getUint16(22, true);
        var sampleRate = view.getUint32(24, true);
        var bitsPerSample = view.getUint16(34, true);
        headerResult.resolve(AudioStreamFormat.getWaveFormatPCM(sampleRate, bitsPerSample, channelCount));
      };

      headerReader.onload = processHeader;
      headerReader.readAsArrayBuffer(header);
      return headerResult.promise();
    };

    this.upload = function (audioNodeId) {
      return _this.turnOn().onSuccessContinueWithPromise(function (_) {
        return _this.privAudioFormatPromise.onSuccessContinueWith(function (format) {
          var fileStream = new ChunkedArrayBufferStream(3200);
          var reader = new FileReader();
          var stream = new ChunkedArrayBufferStream(format.avgBytesPerSec / 10, audioNodeId);
          _this.privStreams[audioNodeId] = stream;

          var processFile = function processFile(event) {
            if (stream.isClosed) {
              return; // output stream was closed (somebody called TurnOff). We're done here.
            }

            stream.writeStreamChunk({
              buffer: reader.result,
              isEnd: false,
              timeReceived: Date.now()
            });
            stream.close();
          };

          reader.onload = processFile;

          reader.onerror = function (event) {
            var errorMsg = "Error occurred while processing '".concat(_this.privFile.name, "'. ").concat(event);

            _this.onEvent(new AudioStreamNodeErrorEvent(_this.privId, audioNodeId, errorMsg));

            throw new Error(errorMsg);
          };

          var chunk = _this.privFile.slice(44);

          reader.readAsArrayBuffer(chunk);
          return stream.getReader();
        });
      });
    };

    this.onEvent = function (event) {
      _this.privEvents.onEvent(event);

      Events.instance.onEvent(event);
    };

    this.privId = audioSourceId ? audioSourceId : createNoDashGuid();
    this.privEvents = new EventSource();
    this.privFile = file; // Read the header.

    this.privAudioFormatPromise = this.readHeader();
  }

  _createClass(FileAudioSource, [{
    key: "format",
    get: function get() {
      return this.privAudioFormatPromise;
    }
  }, {
    key: "events",
    get: function get() {
      return this.privEvents;
    }
  }, {
    key: "deviceInfo",
    get: function get() {
      return this.privAudioFormatPromise.onSuccessContinueWithPromise(function (result) {
        return PromiseHelper.fromResult({
          bitspersample: result.bitsPerSample,
          channelcount: result.channels,
          connectivity: connectivity.Unknown,
          manufacturer: "Speech SDK",
          model: "File",
          samplerate: result.samplesPerSec,
          type: type.File
        });
      });
    }
  }]);

  return FileAudioSource;
}();